import { useState } from 'react'
import { Avatar, Box, Chip, Stack, Typography } from '@mui/material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DataTable, {
  IDataTableColumn,
} from '../../../../components/Tables/DataTable'
import SimpleCard from '../../../../components/Cards/SimpleCard'
import AlertDefault from '../../../../components/Alerts'
import DataTableCellTypography from '../../../../components/Typographys/DataTableCellTypography'
import DataTableSubcellTypography from '../../../../components/Typographys/DataTableSubcellTypography'
import NumberFunctions from '../../../../core/functions/NumberFunctions'
import { dateTimeString } from '../../../../core/functions/dateTime'
import { checkoutOrderCreatedDateColumn } from '../CheckoutOrderColumns/CheckoutOrderColumnsDefault'
import {
  TAnticipationReconciliationListItem,
  TAnticipationReconciliationPagination,
} from '../../../../core/types/AnticipationReconciliation'
import AnticipationReconciliationDetails, {
  closedByLabel,
} from './AnticipationReconciliationDetails'

type TItem = TAnticipationReconciliationListItem
const money = NumberFunctions.formatMoneyDefault

const columns: IDataTableColumn<TItem>[] = [
  {
    ...(checkoutOrderCreatedDateColumn as unknown as IDataTableColumn<TItem>),
    id: 'saleDate',
    label: 'Data da venda',
  },
  {
    id: 'productName',
    label: 'Produto',
    render: (_id, value, row) => (
      <Stack>
        <DataTableCellTypography>{value}</DataTableCellTypography>
        <DataTableSubcellTypography>{row.offerName}</DataTableSubcellTypography>
      </Stack>
    ),
  },
  { id: 'sellerName', label: 'Vendedor' },
  {
    id: 'customerName',
    label: 'Cliente',
    render: (_id, value, row) => (
      <Stack width="140px">
        <DataTableCellTypography>{value}</DataTableCellTypography>
        <DataTableSubcellTypography noWrap>
          {row.customerEmail}
        </DataTableSubcellTypography>
      </Stack>
    ),
  },
  {
    id: 'transactionValue',
    label: 'Valor da transação',
    format: (value) => money(value),
  },
  {
    id: 'estimatedFee',
    label: 'Taxa estimada → real',
    render: (_id, value, row) => (
      <DataTableCellTypography>
        {money(value)} → {money(row.realFee)}
      </DataTableCellTypography>
    ),
  },
  {
    id: 'adjustedValue',
    label: 'Valor ajustado',
    render: (_id, value, row) => (
      <Stack>
        <Typography fontWeight={600} fontSize="14px" color="error.main">
          - {money(value)}
        </Typography>
        <DataTableSubcellTypography>
          {row.adjustedRecipients} recebedor(es)
        </DataTableSubcellTypography>
      </Stack>
    ),
  },
  {
    id: 'checkedAt',
    label: 'Verificado em',
    render: (_id, value, row) => (
      <Stack>
        <DataTableCellTypography>
          {dateTimeString(value as Date)}
        </DataTableCellTypography>
        <DataTableSubcellTypography>
          {closedByLabel(row.closedBy)}
        </DataTableSubcellTypography>
      </Stack>
    ),
  },
  {
    id: 'orderChargeId',
    label: '',
    // ponytail: a linha toda já abre o detalhe; o ícone é só a affordance
    render: () => <VisibilityOutlinedIcon fontSize="small" color="primary" />,
  },
]

function TotalCard({
  label,
  value,
}: {
  label: string
  value: string | number
}) {
  return (
    <SimpleCard sx={{ px: 4 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ width: 40, height: 40, bgcolor: '#F2F2FF' }}>
          <img alt={label} src="/assets/icons/payments/pay.svg" />
        </Avatar>
        <Stack>
          <Typography
            color="#707275"
            fontWeight={500}
            fontSize="14px"
            lineHeight="14px"
          >
            {label}
          </Typography>
          <Typography
            color="#38393B"
            fontWeight={600}
            fontSize="24px"
            lineHeight="32px"
          >
            {value}
          </Typography>
        </Stack>
      </Stack>
    </SimpleCard>
  )
}

interface IAnticipationReconciliationTotalsProps {
  data: TAnticipationReconciliationPagination
  error: string
  clear: () => void
}

/** Alerta + cards: ficam acima das abas, como nas outras abas. */
export function AnticipationReconciliationTotals({
  data,
  error,
  clear,
}: IAnticipationReconciliationTotalsProps) {
  return (
    <>
      {error !== '' && (
        <AlertDefault onClose={clear} severity="error" sx={{ mb: 2 }}>
          {error}
        </AlertDefault>
      )}

      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={3}
        sx={{ mb: 2, px: { xs: 2, lg: 0 } }}
      >
        <TotalCard label="Transações encontradas" value={data.total} />
        <TotalCard
          label="Valor ajustado"
          value={money(NumberFunctions.toNumber(data.info ?? 0))}
        />
      </Stack>
    </>
  )
}

interface IAnticipationReconciliationTabProps {
  data: TAnticipationReconciliationPagination
  page: number
  rowsPerPage: number
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rows: number) => void
}

/** Tabela + detalhe: fica abaixo das abas. */
export default function AnticipationReconciliationTab({
  data,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: IAnticipationReconciliationTabProps) {
  const [current, setCurrent] = useState<string>()

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Chip
          size="small"
          label="Somente transações em que o valor de algum recebedor foi reduzido"
          sx={{ mb: 1, backgroundColor: '#F2F2FF', color: 'primary.main' }}
        />
      </Box>

      <DataTable<TItem>
        columns={columns}
        hasNewCustomHeader
        data={{ ...data, info: undefined }}
        page={page}
        rowsPerPage={rowsPerPage}
        onItemClick={(row) => setCurrent(row.orderChargeId)}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />

      <AnticipationReconciliationDetails
        orderChargeId={current}
        onClose={() => setCurrent(undefined)}
      />
    </>
  )
}
